"use client";

import LinkedInIcon from "@/components/core/icons/LinkedInIcon";
import NextImage from "next/image";
import Link from "next/link";
import clsx from "clsx";

export default function AuthorPreview({
  author,
  className,
  textclassName,
  variant = "small",
  linked = true,
}: {
  author: Author;
  linked?: boolean;
  className?: string;
  textclassName?: string;
  variant?: "small" | "large";
}) {
  const imageUrl = author.imageUrl;
  const authorPagePath =
    linked && author.includeInAuthorsListing && author.slug
      ? `/authors/${author.slug}`
      : undefined;

  const redirectToLinkedIn = () => {
    if (author.linkedinUrl) {
      window.open(author.linkedinUrl, "_blank");
    }
  };

  const chipContent = (
    <span
      className={clsx([
        "font-mono whitespace-nowrap inline-flex items-center truncate line-clamp-1 leading-xxl text-[9px] tracking-xl uppercase font-semibold firefox:!tracking-normal",
        variant === "large" && "lg:text-[13px]",
        textclassName,
      ])}
    >
      {author.name}
      {author.company && (
        <span className="ml-1 opacity-60">({author.company})</span>
      )}
    </span>
  );

  const chipClassName = clsx([
    "ml-0.5 bg-grey-850 cursor-pointer px-2 flex items-center justify-center rounded-sm min-w-0",
    variant === "small" && "h-6",
    variant === "large" && "h-[34px]",
  ]);

  return (
    <div
      className={clsx([
        "mb-2 flex items-center group",
        variant === "large" && "gap-[3px]",
        className,
      ])}
    >
      {imageUrl && (
        <NextImage
          className={clsx([
            "aspect-square rounded-sm shrink-0",
            variant === "small" && "w-6 h-6",
            variant === "large" && "w-[34px] h-[34px]",
          ])}
          alt={author.name}
          src={imageUrl}
          width={64}
          height={64}
        />
      )}
      {authorPagePath ? (
        <Link
          href={authorPagePath}
          title={author.name}
          className={chipClassName}
        >
          {chipContent}
        </Link>
      ) : (
        <div className={chipClassName} onClick={linked ? redirectToLinkedIn : undefined}>
          {chipContent}
        </div>
      )}

      {linked && author.linkedinUrl && (
        <div
          className={clsx([
            "bg-[#0E76A8] transition-all ml-0.5 max-md:hidden rounded-sm opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 select-none pointer-events-none group-hover:pointer-events-auto shrink-0",
            variant === "small" && "w-6 h-6",
            variant === "large" && "w-[34px] h-[34px]",
          ])}
        >
          <Link
            href={author.linkedinUrl}
            title={author.name + ` on Linkedin`}
            target="_blank"
            rel="noreferrer noopener"
            className="flex items-center justify-center h-full w-full"
          >
            <LinkedInIcon />
          </Link>
        </div>
      )}
    </div>
  );
}
